import { isPromptScaffoldingText } from '@crowdex/agent-contracts';
import { readableReferences } from '@crowdex/ui/lib/inline-reference';
import type { UIMessage } from 'ai';

const DEFAULT_TITLE = 'New conversation';
const MAX_WORDS = 8;
const MAX_LENGTH = 80;

function truncate(value: string): string {
  if (value.length <= MAX_LENGTH) return value;
  const shortened = value.slice(0, MAX_LENGTH + 1);
  const lastSpace = shortened.lastIndexOf(' ');
  return `${shortened.slice(0, lastSpace > 20 ? lastSpace : MAX_LENGTH).trim()}…`;
}

export function fallbackConversationTitle(userMessage: string): string {
  const cleaned = readableReferences(userMessage, 'plain')
    .replace(/```[\s\S]*?```/g, ' code ')
    .replace(/[`*_#>]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^(?:bonjour|salut|hello|hi|s'il te plaît|stp|please)[,!:;\s-]*/i, '')
    .trim();
  if (cleaned.length === 0) return DEFAULT_TITLE;
  const words = cleaned.split(' ');
  return truncate(words.length > MAX_WORDS ? `${words.slice(0, MAX_WORDS).join(' ')}…` : cleaned);
}

export function normalizeConversationTitle(value: string, fallback: string): string {
  const firstLine =
    readableReferences(value, 'plain')
      .split(/\r?\n/)
      .find((line) => line.trim().length > 0) ?? '';
  const cleaned = firstLine
    .replace(/^(?:title|titre)\s*:\s*/i, '')
    .replace(/^["'“”‘’`]+|["'“”‘’`]+$/g, '')
    .replace(/[.!?:;,\s-]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.length < 3 ? fallback : truncate(cleaned);
}

/** Agent guidance and session bootstraps are prompt plumbing and must never become titles. */
export function isAgentGuidanceTitle(value: string): boolean {
  return isPromptScaffoldingText(value);
}

export function normalizeReportedConversationTitle(value: string, fallback: string): string {
  return isAgentGuidanceTitle(value) ? fallback : normalizeConversationTitle(value, fallback);
}

/**
 * The title a turn leaves its conversation with: the one its agent reported, else its current
 * one, else (still untitled) one made of the user's first message.
 */
export function turnConversationTitle(
  current: string,
  messages: readonly UIMessage[],
  reported: string | undefined,
): string {
  const firstUserText = messages
    .find((message) => message.role === 'user')
    ?.parts.find((part) => part.type === 'text')?.text;
  const fallback = firstUserText ? fallbackConversationTitle(firstUserText) : DEFAULT_TITLE;
  if (reported !== undefined) return normalizeReportedConversationTitle(reported, fallback);
  return current === DEFAULT_TITLE || isAgentGuidanceTitle(current) ? fallback : current;
}
